import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { calculateBmi } from "../../../convex/lib/engine";
import type { UnitHeight } from "../types";
import { cmToFtIn, ftInToCm } from "../types";
import { BmiCard } from "./BmiCard";
import { ScreenShell } from "./ScreenShell";

const MIN_HEIGHT_CM = 120;
const MAX_HEIGHT_CM = 230;

function formatDisplay(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function digitsOnly(raw: string): string {
  return raw.replace(/[^0-9]/g, "");
}

interface HeightStepProps {
  readonly title: string;
  readonly valueCm?: number;
  readonly unit: UnitHeight;
  readonly onUnitChange: (unit: UnitHeight) => void;
  readonly onContinue: (heightCm: number) => void;
  /** Current weight; when provided, a live BMI card appears once height is valid. */
  readonly weightKg?: number;
}

/** Height input with a cm / ft-in segmented toggle that converts in place; canonical value is cm. */
export function HeightStep({ title, valueCm, unit, onUnitChange, onContinue, weightKg }: HeightStepProps) {
  const [cmDisplay, setCmDisplay] = useState(() =>
    valueCm !== undefined && unit === "cm" ? formatDisplay(valueCm) : "",
  );
  const [ftInDisplay, setFtInDisplay] = useState(() => {
    if (valueCm === undefined || unit !== "ftin") return { ft: "", inch: "" };
    const { ft, inch } = cmToFtIn(valueCm);
    return { ft: String(ft), inch: String(inch) };
  });

  const parsedCm = cmDisplay.trim() === "" ? undefined : Number(cmDisplay);
  const parsedFt = ftInDisplay.ft.trim() === "" ? undefined : Number(ftInDisplay.ft);
  const parsedInch = ftInDisplay.inch.trim() === "" ? undefined : Number(ftInDisplay.inch);

  const candidateCm =
    unit === "cm"
      ? parsedCm !== undefined && Number.isFinite(parsedCm)
        ? parsedCm
        : undefined
      : parsedFt !== undefined || parsedInch !== undefined
        ? ftInToCm(parsedFt ?? 0, parsedInch ?? 0)
        : undefined;
  const inRange =
    candidateCm !== undefined && candidateCm >= MIN_HEIGHT_CM && candidateCm <= MAX_HEIGHT_CM;

  const handleUnitChange = (next: UnitHeight) => {
    if (next === unit) return;
    if (candidateCm !== undefined) {
      if (next === "ftin") {
        const { ft, inch } = cmToFtIn(candidateCm);
        setFtInDisplay({ ft: String(ft), inch: String(inch) });
      } else {
        setCmDisplay(formatDisplay(candidateCm));
      }
    }
    console.log("[Frontend][HeightStep] Unit toggled", { from: unit, to: next });
    onUnitChange(next);
  };

  const rangeHint =
    unit === "cm"
      ? `Enter a height between ${MIN_HEIGHT_CM} and ${MAX_HEIGHT_CM} cm.`
      : (() => {
          const min = cmToFtIn(MIN_HEIGHT_CM);
          const max = cmToFtIn(MAX_HEIGHT_CM);
          return `Enter a height between ${min.ft} ft ${min.inch} in and ${max.ft} ft ${max.inch} in.`;
        })();

  return (
    <ScreenShell
      title={title}
      center
      footer={
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!inRange}
          onClick={() => {
            if (inRange && candidateCm !== undefined) onContinue(candidateCm);
          }}
        >
          Continue
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-6">
        {unit === "cm" ? (
          <div className="flex items-end justify-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              aria-label={title}
              value={cmDisplay}
              onChange={(e) => setCmDisplay(digitsOnly(e.target.value))}
              placeholder="175"
              className="w-48 rounded-2xl border border-border bg-surface px-4 py-3 text-center text-5xl font-bold tabular-nums text-text placeholder:text-text-muted/40 focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <span className="pb-4 text-lg font-medium text-text-muted">cm</span>
          </div>
        ) : (
          <div className="flex items-end justify-center gap-4">
            <div className="flex items-end justify-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                aria-label="Height in feet"
                value={ftInDisplay.ft}
                onChange={(e) => setFtInDisplay((prev) => ({ ...prev, ft: digitsOnly(e.target.value) }))}
                placeholder="5"
                className="w-24 rounded-2xl border border-border bg-surface px-4 py-3 text-center text-5xl font-bold tabular-nums text-text placeholder:text-text-muted/40 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <span className="pb-4 text-lg font-medium text-text-muted">ft</span>
            </div>
            <div className="flex items-end justify-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                aria-label="Height in inches"
                value={ftInDisplay.inch}
                onChange={(e) => setFtInDisplay((prev) => ({ ...prev, inch: digitsOnly(e.target.value) }))}
                placeholder="9"
                className="w-24 rounded-2xl border border-border bg-surface px-4 py-3 text-center text-5xl font-bold tabular-nums text-text placeholder:text-text-muted/40 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <span className="pb-4 text-lg font-medium text-text-muted">in</span>
            </div>
          </div>
        )}
        <div
          role="group"
          aria-label="Height unit"
          className="flex rounded-xl border border-border bg-surface p-1"
        >
          {(["cm", "ftin"] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={unit === option}
              onClick={() => handleUnitChange(option)}
              className={cn(
                "min-h-[44px] min-w-[72px] rounded-lg px-4 text-base font-medium",
                "select-none transition-all duration-200 ease-out active:scale-[0.98]",
                unit === option ? "bg-accent text-white" : "text-text-muted",
              )}
            >
              {option === "cm" ? "cm" : "ft/in"}
            </button>
          ))}
        </div>
        {candidateCm !== undefined && !inRange && (
          <p role="alert" className="text-sm text-danger">
            {rangeHint}
          </p>
        )}
        {inRange && candidateCm !== undefined && weightKg !== undefined && (() => {
          const bmi = calculateBmi({ weightKg, heightCm: candidateCm })?.value;
          return bmi ? <BmiCard bmi={bmi.bmi} classification={bmi.classification} /> : null;
        })()}
      </div>
    </ScreenShell>
  );
}
